import React, { useState, useRef, useCallback, useLayoutEffect, useContext } from "react";
import { useFloating, autoUpdate, offset, flip, shift, arrow, useHover, useFocus, useDismiss, useRole, useInteractions, FloatingPortal, useClick, FloatingArrow } from "@floating-ui/react";
import { PopupContext } from "../Popup/PopupContext";

/**
 * Tooltip — Hover/focus tooltip using Floating UI for auto-positioning.
 *
 * Automatically flips and shifts to stay within the viewport.
 * Renders via a portal so it's never clipped by overflow:hidden parents.
 *
 * @param {ReactNode|Function} tooltipText     - Content to display (string, JSX, or render function)
 * @param {string}             placement        - Preferred position: 'top', 'bottom', 'left', 'right' etc.
 * @param {string}             theme            - 'dark' (default) or 'light' — sets background/text colors
 * @param {number}             offsetDistance   - Gap between trigger element and tooltip (px)
 * @param {boolean}            showArrow        - Show the triangular arrow pointing to the trigger
 * @param {Object}             delay            - { open, close } delays in ms (prevents flicker on fast mouse moves)
 * @param {boolean}            enableTooltip    - Enable tooltip (default: true)
 * @param {boolean}            showWithoutOverflowing - Show tooltip even if trigger element overflows its container
 * @param {Object}             customStyle      - Custom styles for the tooltip container
 * @param {Object}             arrowStyle       - Custom styles for the tooltip arrow
 */
export const Tooltip = ({
  children,
  tooltipText,
  placement = "top",
  theme = "dark",
  offsetDistance = 8,
  showArrow = true,
  delay = { open: 200, close: 100 },
  enableTooltip = true,
  showWithoutOverflowing = false,
  fontClass = "body-s",
  customStyle = {},
  customClassName = "",
  arrowStyle = {},
  wrapperStyle = {},
  cursorPointerOnOverflow = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const triggerRef = useRef(null);
  const arrowRef = useRef(null);
  const popupCtx = useContext(PopupContext);

  // Measure overflow status on mount and resize
  useLayoutEffect(() => {
    if (showWithoutOverflowing) return;
    
    const trigger = triggerRef.current;
    if (trigger) {
      const checkOverflow = () => {
        const target = trigger.firstElementChild || trigger;
        if (target) {
          // Use Math.ceil and a small buffer (1px) to avoid false positives from subpixel rounding
          setIsOverflowing(Math.ceil(target.scrollWidth) > target.clientWidth + 1);
        }
      };
      
      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }
  }, [showWithoutOverflowing, children]);

  const shouldShow = enableTooltip && (showWithoutOverflowing || isOverflowing);

  // Floating UI setup — handles positioning, collision avoidance, and arrow placement
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate, // Recalculates position on scroll/resize
    middleware: [
      offset(offsetDistance),          // Gap between trigger and tooltip
      flip(),                          // Flip to opposite side if not enough space
      shift(),                         // Shift along axis to stay in viewport
      showArrow ? arrow({ element: arrowRef }) : null,
    ].filter(Boolean),
  });

  // Interaction hooks — each one adds a specific trigger behavior
  const hover = useHover(context, { move: false, delay, enabled: enableTooltip });
  const focus = useFocus(context, { enabled: enableTooltip });     // Open on keyboard focus (accessibility)
  const dismiss = useDismiss(context, { enabled: enableTooltip }); // Close on Escape or outside click
  const role = useRole(context, { role: "tooltip" }); // Sets aria-describedby

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  /** Wraps children in a span with the Floating UI reference props */
  const renderReference = () => {
    return (
      <span
        ref={(node) => {
          refs.setReference(node);
          triggerRef.current = node;
        }}
        {...getReferenceProps()}
        className="tooltip-trigger-wrapper"
        style={{
          ...wrapperStyle,
          ...(isOverflowing && cursorPointerOnOverflow ? { cursor: 'pointer' } : {})
        }}
      >
        {children}
      </span>
    );
  };

  return (
    <>
      {renderReference()}
      <FloatingPortal>
        {isOpen && shouldShow && (
          <div
            className={`tooltip-container tooltip-container-${theme} ${fontClass}${customClassName ? ` ${customClassName}` : ""}`}
            ref={refs.setFloating}
            style={{ ...floatingStyles, ...(popupCtx?.zIndex ? { zIndex: popupCtx.zIndex + 10 } : {}), ...customStyle }}
            {...getFloatingProps()}
          >
            {/* tooltipText can be a render function for complex tooltip content */}
            {typeof tooltipText === "function" ? tooltipText() : tooltipText}
            {showArrow && (
              <FloatingArrow
                ref={arrowRef}
                context={context}
                className={`tooltip-arrow tooltip-arrow-${theme}`}
                style={arrowStyle}
                width={12}
                height={6}
                {...(theme === "light" ? { stroke: "var(--color-border-subtle)", strokeWidth: 0.5 } : {})}
              />
            )}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

// ─── Popover ────────────────────────────────────────────────────────────

/**
 * Popover — Click-triggered popup with optional title, using Floating UI.
 *
 * Unlike Tooltip (hover), Popover opens on click and stays open until
 * the user clicks elsewhere or presses Escape. Good for richer content
 * like descriptions, forms, or confirmation dialogs.
 *
 * @param {ReactNode|Function} tooltipText   - Body content
 * @param {ReactNode|Function} PopoverTitle  - Optional title rendered above body
 * @param {string}             placement     - Preferred position (default: 'bottom')
 * @param {string}             theme         - 'light' (default) or 'dark'
 */
export const Popover = ({
  children,
  tooltipText,
  PopoverTitle,
  placement = "bottom",
  theme = "light",
  offsetDistance = 8,
  showArrow = true,
  fontClass = "body-s",
  customStyle = {},
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const arrowRef = useRef(null);
  const popupCtx = useContext(PopupContext);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetDistance),
      flip(),
      shift(),
      showArrow ? arrow({ element: arrowRef }) : null,
    ].filter(Boolean),
  });

  // Popover uses click instead of hover
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const renderReference = () => {
    return (
      <span
        ref={(node) => {
          refs.setReference(node);
          triggerRef.current = node;
        }}
        {...getReferenceProps()}
        style={{ display: "inline-block", width: "max-content", maxWidth: "100%", verticalAlign: "middle" }}
      >
        {children}
      </span>
    );
  };

  return (
    <>
      {renderReference()}
      <FloatingPortal>
        {isOpen && (
          <div
            className={`tooltip-container tooltip-container-${theme} ${fontClass}`}
            ref={refs.setFloating}
            style={{ ...floatingStyles, pointerEvents: 'auto', ...(popupCtx?.zIndex ? { zIndex: popupCtx.zIndex + 10 } : {}), ...customStyle }}
            {...getFloatingProps()}
          >
            {/* Optional header section */}
            {PopoverTitle && (
              <div className="tooltip-title">
                {typeof PopoverTitle === "function" ? PopoverTitle() : PopoverTitle}
              </div>
            )}
            {/* Body content */}
            <div>
              {typeof tooltipText === "function" ? tooltipText() : tooltipText}
            </div>
            {showArrow && (
              <FloatingArrow
                ref={arrowRef}
                context={context}
                className={`tooltip-arrow tooltip-arrow-${theme}`}
                width={12}
                height={6}
                {...(theme === "light" ? { stroke: "var(--color-border-subtle)", strokeWidth: 0.5 } : {})}
              />
            )}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export default Tooltip;
